/**
 * Entidad Usuario — modelo puro del dominio. Sin React, sin React Query.
 *
 * Alineado al contrato real del backend (`ani-fiscalizacion-api`,
 * `prisma/schema.prisma:82-204` + `GET /auth/me`):
 *
 *  - `Rol` es un objeto `{ id, nombre, nivel }` (no enum string). El `nivel`
 *    1..4 es la fuente de verdad para la jerarquía (ADR-015 §3).
 *  - `EstadoUsuario` es PascalCase, como lo devuelve Prisma.
 *  - `Usuario.id` es `number` (autoincrement en BD).
 *  - `Usuario.permisos` son los EFECTIVOS resueltos por el backend
 *    (`(Rol.permisos ∪ Perfil.permisos ∪ grants) − revokes`, ADR-015 §5).
 *    Para SuperAdmin viene `['*']` (wildcard, no expandido).
 */

export type EstadoUsuario = 'Activo' | 'Inactivo' | 'Suspendido' | 'PendienteVerificacion';

/**
 * Niveles de la jerarquía de roles (fuente de verdad de la autorización).
 * Internos 1..4; los actores externos se ubican **por debajo** del auxiliar
 * interno: participante privado líder en `0`, auxiliar de campo en `-1`
 * (ADR-020/021), y el solicitante (solo lectura) en `-2`, el más bajo.
 */
export type NivelRol = -2 | -1 | 0 | 1 | 2 | 3 | 4;

export type Rol = {
  id: number;
  nombre: string;
  nivel: NivelRol;
};

export type Perfil = {
  id: number;
  nombre: string;
};

/**
 * Shape completo de un Usuario administrativo. Es lo que devuelve
 * `GET /admin/users/:id` cuando el backend exponga el shape completo;
 * mientras tanto, el listado actual (`/admin/users`) devuelve solo
 * los campos `id, email, nombre, nombreCompleto, telefono, estado,
 * perfilId, ultimoLoginEn, creadoEn` (ver gap §6 del HANDOFF).
 */
export type Usuario = {
  id: number;
  email: string;
  nombre: string;
  nombreCompleto: string | null;
  telefono: string | null;
  estado: EstadoUsuario;
  rolId: number;
  rol?: Rol;
  perfilId: number | null;
  perfil?: Perfil | null;
  ultimoLoginEn: string | null;
  creadoEn: string;
  actualizadoEn?: string;
  /**
   * Permisos EFECTIVOS del usuario `(rol ∪ perfil ∪ grants) − revokes`
   * (ADR-015 §5). Solo lo devuelve el DETALLE (`GET /admin/users/:id`),
   * no el listado. Lo consume el panel de permisos individuales para
   * saber qué permisos heredados puede revocar.
   */
  permisos?: string[];
};

/**
 * Subconjunto del Usuario que circula en sesión. Lo popula `/auth/me`
 * tras login/verify-mfa.
 */
export type SessionUser = {
  id: number;
  email: string;
  nombre: string;
  estado: EstadoUsuario;
  rol: Rol;
  perfil: Perfil | null;
  permisos: string[];
  /**
   * Estado de MFA para el usuario actual (ADR-016). Lo entrega
   * `/auth/me` y el FE lo usa para:
   *  - decidir si mostrar el setup card (enabled=false + required=true).
   *  - mostrar banner de grace period (gracePeriodEndsAt != null).
   *  - habilitar el botón de "ya tengo MFA" en cuenta/seguridad.
   */
  mfa?: {
    enabled: boolean;
    methods: Array<'totp' | 'backup_code' | 'webauthn'>;
    required: boolean;
    /** ISO timestamp. null si ya enroló o si el rol no exige. */
    gracePeriodEndsAt: string | null;
  };
};

export type Sesion = {
  id: string;
  clientType: 'web' | 'mobile';
  familyId: string;
  createdAt: string;
  expiresAt: string;
  revokedAt: string | null;
};

/**
 * Permiso individual aplicado sobre un usuario (`UsuarioPermiso`, ADR-015 §4).
 *
 *  - `grant`: agrega un permiso al efectivo, opcionalmente con `expiresAt`.
 *  - `revoke`: resta un permiso heredado de rol/perfil. Siempre permanente
 *    (su `expiresAt` siempre es `null`).
 *
 * `otorgadoPor` es el admin que creó el registro (auditoría visible al
 * panel de Fase 14).
 */
export type UsuarioPermisoTipo = 'grant' | 'revoke';

export type UsuarioPermiso = {
  id: string;
  permisoNombre: string;
  tipo: UsuarioPermisoTipo;
  expiresAt: string | null;
  otorgadoPor: { id: number; nombre: string };
  creadoEn: string;
};

/** Resultado canónico de una autenticación (login + verify-mfa). */
export type AuthenticatedSession = {
  accessToken: string;
  user: SessionUser;
};

/* ============== Reglas puras del dominio ============== */

export function estaActivo(u: Pick<Usuario | SessionUser, 'estado'>): boolean {
  return u.estado === 'Activo';
}

export function puedeIniciarSesion(u: Pick<Usuario | SessionUser, 'estado'>): boolean {
  return u.estado === 'Activo';
}

export function requiereActivacion(u: Pick<Usuario | SessionUser, 'estado'>): boolean {
  return u.estado === 'PendienteVerificacion';
}

/** Niveles canónicos del seed (`prisma/seed.ts:90-95`). */
export const NIVEL_SUPER_ADMIN: NivelRol = 4;
export const NIVEL_ADMIN: NivelRol = 3;
export const NIVEL_SUPERVISOR: NivelRol = 2;
export const NIVEL_AUXILIAR: NivelRol = 1;
/** Participante privado líder (concesionario, actor externo). */
export const NIVEL_PARTICIPANTE_PRIVADO: NivelRol = 0;
/** Participante privado auxiliar (concesionario de campo, externo móvil). */
export const NIVEL_PARTICIPANTE_PRIVADO_AUXILIAR: NivelRol = -1;
/** Solicitante del proyecto (actor externo de solo lectura: ICE pública o iniciativa privada). */
export const NIVEL_SOLICITANTE: NivelRol = -2;

/**
 * ¿El usuario es participante privado (actor externo)? Cubre al líder (nivel 0)
 * y al auxiliar (-1) — todo nivel por debajo de los roles internos (≥ 1). Se usa
 * para enrutar sus deep-links al módulo de incidencias, único al que tiene
 * acceso, en vez del detalle de proyecto (al que no puede entrar).
 */
export function esParticipantePrivado(user: Pick<SessionUser, 'rol'> | null | undefined): boolean {
  return user != null && user.rol.nivel <= NIVEL_PARTICIPANTE_PRIVADO;
}

/**
 * ¿El usuario es Administrador (nivel 3) o Super Administrador (nivel 4)? Son
 * los únicos roles con acceso a módulos transversales de toda la organización
 * (p. ej. Reportes, que agrega KPIs de todo el portafolio). Espeja la
 * restricción del backend y sirve para ocultar el módulo del nav y bloquear la
 * ruta antes del montaje.
 */
export function esAdminOSuperior(user: Pick<SessionUser, 'rol'> | null | undefined): boolean {
  return user != null && user.rol.nivel >= NIVEL_ADMIN;
}

/**
 * ¿El usuario es Supervisor (nivel 2)? El Supervisor tiene un alcance operativo
 * acotado: solo Proyectos e Incidencias. No accede a Dashboard, Catálogos ni
 * Usuarios. Espeja la restricción del backend y sirve para ocultar esos módulos
 * del nav, redirigir su landing a Proyectos y bloquear sus rutas antes del
 * montaje.
 */
export function esSupervisor(user: Pick<SessionUser, 'rol'> | null | undefined): boolean {
  return user != null && user.rol.nivel === NIVEL_SUPERVISOR;
}

/**
 * ¿El usuario es Auxiliar (nivel 1)? El Auxiliar trabaja en campo sobre
 * incidencias (reporta, adjunta evidencia, verifica el trabajo del PP); no
 * tiene alcance de portafolio, así que su home es Incidencias y no ve el
 * Dashboard. Misma excepción de UX que `esSupervisor`: solo para items sin
 * `permission` y para el landing — la autorización real es por permiso.
 */
export function esAuxiliar(user: Pick<SessionUser, 'rol'> | null | undefined): boolean {
  return user != null && user.rol.nivel === NIVEL_AUXILIAR;
}
